import React, { PureComponent, createRef, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as instructorActions from '../../_actions/roster.actions'
import _ from 'lodash'
import dayjs from 'dayjs'
import { DatePicker } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { Accordion, AccordionTab } from 'primereact/accordion'
import { Button } from 'primereact/button'
import { MultiSelect } from 'primereact/multiselect'
import CustomSpinner from '../common/CustomSpinner'
import EmployeeMultiSelect from '../common/EmployeeMultiSelect'
import { viewConstants } from '../../_constants/view.constants'

// ─── Shared style tokens (mirrors roster-frontend ShiftToolbar) ───────────────
const BTN_BASE = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: '1px solid #E5E7EB',
    background: '#fff',
    color: '#374151',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    padding: '0 12px',
    height: 32,
    gap: 6,
    whiteSpace: 'nowrap',
    lineHeight: 1,
    outline: 'none',
}

const BTN_ACTIVE = {
    ...BTN_BASE,
    border: '1px solid #2563EB',
    background: '#EFF6FF',
    color: '#2563EB',
    fontWeight: 600,
}

// ─── Single-select pill dropdown (Department / Branch) ────────────────────────
function FilterChip({ label, value, options, onChange }) {
    const [open, setOpen] = React.useState(false);
    const containerRef = useRef(null);

    const sel = options.find(o => String(o.value) === String(value))
    const active = value !== undefined && value !== null && value !== ''

    const handleToggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(!open);
    };

    const handleSelect = (val) => {
        onChange(val);
        setOpen(false);
    };

    const handleClickOutside = (e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
            setOpen(false);
        }
    };

    React.useEffect(() => {
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [open]);

    return (
        <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                type="button"
                style={active ? BTN_ACTIVE : BTN_BASE}
                onClick={handleToggle}
            >
                {sel ? sel.label : label}
                <DownOutlined style={{ fontSize: 9, opacity: 0.55 }} />
            </button>

            {open && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: 4,
                        minWidth: 120,
                        background: 'white',
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        padding: '4px 0',
                    }}
                >
                    <div
                        style={{
                            padding: '8px 12px',
                            color: '#9CA3AF',
                            fontSize: 13,
                            cursor: 'pointer',
                        }}
                        onClick={() => handleSelect(undefined)}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                        All
                    </div>
                    {options.map(o => (
                        <div
                            key={String(o.value)}
                            style={{
                                padding: '8px 12px',
                                fontSize: 13,
                                cursor: 'pointer',
                            }}
                            onClick={() => handleSelect(o.value)}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                            {o.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


// ─── Employee multi-select pill dropdown ──────────────────────────────────────
// (now handled by EmployeeMultiSelect component)

class Filter extends PureComponent {
    state = {
        _isMounted: false,
        selectedDept: undefined,
        selectedBranch: undefined,
        startDate: null,
        endDate: null,
    }

    componentDidMount() {
        this._isMounted = true
        if (this.props.employees.length === 0) {
            this.props.actions.fetchEmployeeList()
        }
    }

    componentWillUnmount() {
        this._isMounted = false
    }

    componentDidUpdate(prevProps) {
        if (prevProps.view !== this.props.view) {
            this.resetLocalState();
        }
    }

    resetLocalState = () => {
        this.setState({ selectedDept: undefined, selectedBranch: undefined, startDate: null, endDate: null });
        this.props.actions.selectDateRangeFilter({ startDate: null, endDate: null, hasActive: false });
    };

    handleDateRangeChange = (dates) => {
        const [start, end] = dates;
        const has = !!(start ?? end);
        this.setState({ startDate: start ?? null, endDate: end ?? null });
        this.props.actions.selectDateRangeFilter({
            startDate: start ?? null,
            endDate: end ?? null,
            hasActive: has,
        });
        this.fetchWithCurrentFilters(null, start ?? null, end ?? null);
    };

    clearDateRange = () => {
        this.setState({ startDate: null, endDate: null });
        this.props.actions.selectDateRangeFilter({
            startDate: null,
            endDate: null,
            hasActive: false,
        });
        this.fetchWithCurrentFilters(null, null, null);
    };

    // Format a moment or dayjs object as local YYYY-MM-DD (no UTC offset)
    getLocalDateStr = (dj) => {
        if (!dj) return null;
        // antd DatePicker returns moment objects
        if (typeof dj === 'object' && dj.format) {
            return dj.format('YYYY-MM-DD');
        }
        // dayjs fallback
        return dayjs(dj).format('YYYY-MM-DD');
    };

    // Format a dayjs or Date as ISO datetime string for API (UTC based)
    getApiDateTime = (dj) => {
        return dj ? dj.toDate().toISOString() : null;
    };

fetchWithCurrentFilters = (selectedEmployeesOverride, startDateOverride, endDateOverride) => {
        const selectedEmployees = selectedEmployeesOverride || this.props.selectedEmployees;
        const { view, currentDate, currentWeekDate } = this.props;
        const isMonth = view !== 'week';
        const options = {
            range: isMonth ? viewConstants.MONTH : viewConstants.WEEK,
            time: isMonth ? currentDate : currentWeekDate,
        };

        // Use local YYYY-MM-DD date strings (no UTC offset shift)
        const startDate = startDateOverride ?? this.state.startDate;
        const endDate = endDateOverride ?? this.state.endDate;
        if (startDate) options.startDate = this.getLocalDateStr(startDate);
        if (endDate) options.endDate = this.getLocalDateStr(endDate);

        if (!_.isEmpty(selectedEmployees)) {
            const cond = { employees: selectedEmployees };
            if (isMonth) {
                this.props.actions.fetchUserETime(_.merge({}, cond, options));
            } else {
                this.props.actions.fetchUpdateWorkETime(_.merge({}, cond, options));
            }
        } else {
            // No employees selected - fetch all shifts
            if (isMonth) {
                this.props.actions.fetchUserETime(options);
            } else {
                this.props.actions.fetchUpdateWorkETime(options);
            }
        }
    };

    selectedEmployeeIds = () => {
        return this.props.selectedEmployees
    }

displayEmployees = () => {
        return (this.props.displayEmployees || []).map(emp => ({
            label: emp.EmployeeName,
            value: emp.key,
        }))
    }

    displayEmployeeIds = () => {
        return (this.props.displayEmployees || []).map(emp => emp.EmployeeAutoID)
    }

// ── Helper to get employee IDs from filtered employee list ─────────────────
    getFilteredEmployeeIds = (branch, department) => {
        const { employees, selectedDepartments, selectedBranches } = this.props;
        
        let filtered = employees || [];
        
        const actualBranch = branch !== undefined ? branch : (selectedBranches || []);
        const actualDept = department !== undefined ? department : (selectedDepartments || []);
        
        if (!_.isEmpty(actualBranch)) {
            filtered = _.filter(filtered, emp => _.includes(actualBranch, emp.EmployeeBranch));
        }
        if (!_.isEmpty(actualDept)) {
            filtered = _.filter(filtered, emp => _.includes(actualDept, emp.EmployeeDepartment));
        }
        
        return filtered.map(emp => emp.key);
    }

    // ── Branch chip (single-select) ───────────────────────────────────────────
    handleBranchChange = async branchValue => {
        if (!this._isMounted) return;
        const branch = branchValue !== undefined ? [branchValue] : []
        this.setState({ selectedBranch: branchValue })

        await this.props.actions.selectEmployees([])
        await this.props.actions.selectBranches(branch)

        let filterCond = {}
        if (!_.isEmpty(branch)) filterCond.EmployeeBranch = branch
        if (!_.isEmpty(this.props.selectedDepartments)) filterCond.EmployeeDepartment = this.props.selectedDepartments

        await this.props.actions.updateDisplayEmployee(filterCond)

        if (!this._isMounted) return;

        // Query with filtered employee IDs
        const employeeIds = this.getFilteredEmployeeIds(branch, undefined);
        this.fetchWithCurrentFilters(employeeIds, this.state.startDate, this.state.endDate);
    }

    // ── Department chip (single-select) ──────────────────────────────────────
    handleDepartmentChange = async deptValue => {
        if (!this._isMounted) return;
        const department = deptValue !== undefined ? [deptValue] : []
        this.setState({ selectedDept: deptValue })

        await this.props.actions.selectEmployees([])
        await this.props.actions.selectDepartments(department)

        let filterCond = {}
        if (!_.isEmpty(department)) filterCond.EmployeeDepartment = department
        if (!_.isEmpty(this.props.selectedBranches)) filterCond.EmployeeBranch = this.props.selectedBranches

        await this.props.actions.updateDisplayEmployee(filterCond)

        if (!this._isMounted) return;

        // Query with filtered employee IDs
        const employeeIds = this.getFilteredEmployeeIds(undefined, department);
        this.fetchWithCurrentFilters(employeeIds, this.state.startDate, this.state.endDate);
    }

handleReset = async () => {
        this.setState({ selectedDept: undefined, selectedBranch: undefined, startDate: null, endDate: null })
        await this.props.actions.selectEmployees([])
        await this.props.actions.selectBranches([])
        await this.props.actions.selectDepartments([])
        await this.props.actions.selectDateRangeFilter({ startDate: null, endDate: null, hasActive: false })
        await this.props.actions.updateDisplayEmployee({ EmployeeDepartment: [], EmployeeBranch: [] })
        this.fetchWithCurrentFilters(null, null, null);
    }

    handleEmployeeChange = value => {
        this.props.actions.selectEmployees(value)
        this.fetchWithCurrentFilters(value, this.state.startDate, this.state.endDate);
    }

    render() {
        const { horizontal } = this.props
        const { selectedDept, selectedBranch, startDate, endDate } = this.state

        // Build options for chips from redux state
        const branchOpts = (this.props.branches || []).map(b => ({
            value: b.value !== undefined ? b.value : b.CompanyBranchAutoID,
            label: b.label !== undefined ? b.label : b.CompanyBranch,
        }))

        const deptOpts = (this.props.departments || []).map(d => ({
            value: d.value !== undefined ? d.value : d.CompanyDepartmentAutoID,
            label: d.label !== undefined ? d.label : d.CompanyDepartment,
        }))

        const employeeOpts = this.displayEmployees()
        const selectedEmployees = this.props.selectedEmployees || []

        const hasActive = selectedDept != null || selectedBranch != null || selectedEmployees.length > 0 || (startDate != null || endDate != null)

        if (horizontal) {
            return (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        flexWrap: 'wrap',
                        padding: '5px 0',
                    }}
                >
                    {/* Employee multi-select */}
                    <EmployeeMultiSelect
                        value={selectedEmployees}
                        options={employeeOpts}
                        onChange={this.handleEmployeeChange}
                        placeholder="All employees"
                        disabled={employeeOpts.length === 0}
                        style={{ minWidth: 200 }}
                    />

                    {/* Date range */}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <DatePicker
                            placeholder="Start date"
                            value={this.state.startDate}
                            onChange={(date) => {
                                const nextStart = date ?? null
                                const nextEnd = this.state.endDate
                                this.handleDateRangeChange([nextStart, nextEnd])
                            }}
                            style={{ width: 120 }}
                            allowClear
                            format="YYYY-MM-DD"
                        />
                        <span style={{ color: '#999' }}>-</span>
                        <DatePicker
                            placeholder="End date"
                            value={this.state.endDate}
                            onChange={(date) => {
                                const nextStart = this.state.startDate
                                const nextEnd = date ?? null
                                this.handleDateRangeChange([nextStart, nextEnd])
                            }}
                            style={{ width: 120 }}
                            allowClear
                            format="YYYY-MM-DD"
                        />
                    </span>

                    {/* Department pill */}
                    {deptOpts.length > 0 && (
                        <FilterChip
                            label="Department"
                            value={selectedDept}
                            options={deptOpts}
                            onChange={this.handleDepartmentChange}
                        />
                    )}

                    {/* Branch pill */}
                    {branchOpts.length > 0 && (
                        <FilterChip
                            label="Branch"
                            value={selectedBranch}
                            options={branchOpts}
                            onChange={this.handleBranchChange}
                        />
                    )}

                    {/* Reset — only shown when something is active */}
                    {hasActive && (
                        <button
                            onClick={this.handleReset}
                            style={{
                                ...BTN_BASE,
                                color: '#DC2626',
                                border: '1px solid #FECACA',
                                background: '#FEF2F2',
                                fontSize: 12,
                            }}
                        >
                            Reset
                        </button>
                    )}
                </div>
            )
        }

        // ── Vertical / accordion mode (unchanged) ─────────────────────────────
        const employeeSelector = (
            <MultiSelect
                value={selectedEmployees}
                options={employeeOpts}
                onChange={e => this.handleEmployeeChange(e.value)}
                placeholder="Select employees"
                filter
                className="w-full"
                maxSelectedLabels={3}
            />
        )

        return (
            <div className="filter-panel-content">
                {this.props.loading ? (
                    <CustomSpinner />
                ) : (
                    <div>
                        <Accordion multiple activeIndex={[0, 1, 2]}>
                            <AccordionTab header="Employee">
                                {employeeSelector}
                            </AccordionTab>
                            <AccordionTab header="Branch">
                                <MultiSelect
                                    value={this.props.selectedBranches || []}
                                    options={branchOpts}
                                    onChange={e => this.props.actions.selectBranches(e.value)}
                                    placeholder="Select branches"
                                    filter
                                    className="w-full"
                                    maxSelectedLabels={3}
                                />
                            </AccordionTab>
                            <AccordionTab header="Department">
                                <MultiSelect
                                    value={this.props.selectedDepartments || []}
                                    options={deptOpts}
                                    onChange={e => this.props.actions.selectDepartments(e.value)}
                                    placeholder="Select departments"
                                    filter
                                    className="w-full"
                                    maxSelectedLabels={3}
                                />
                            </AccordionTab>
                        </Accordion>
                        <div className="m-4">
                            <Button
                                label="Reset"
                                onClick={this.handleReset}
                                className="p-button-secondary p-button-text"
                            />
                        </div>
                    </div>
                )}
            </div>
        )
    }
}

function mapStateToProps(state) {
    return {
        loading: state.roster.loadingEmpoyeeList,
        currentDate: state.roster.currentDate,
        currentWeekDate: state.roster.currentWeekDate,
        employees: state.roster.employeeList,
        displayEmployees: state.roster.displayEmployees,
        selectedEmployees: state.roster.selectedEmployees,
        selectedBranches: state.roster.selectedBranches,
        selectedDepartments: state.roster.selectedDepartments,
        hasActiveDateRangeFilter: state.roster.hasActiveDateRangeFilter,
        branches: state.BranchReducer ? state.BranchReducer.branchList : [],
        departments: state.DepartmentReducer ? state.DepartmentReducer.departmentList : [],
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(instructorActions, dispatch),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Filter)
